import React, { useState, useEffect } from 'react';
import SharedCard from './SharedCard';
import SharedInput from './SharedInput';
import SharedButton from './SharedButton';
import SharedSelect from './SharedSelect';
import ImageUploader from './ImageUploader';
import axios from 'axios';

const ProductManagementForm = () => {
  const [products, setProducts]                     = useState([]);
  const [categories, setCategories]                 = useState([]);
  const [loading, setLoading]                       = useState(true);
  const [productName, setProductName]               = useState('');
  const [productCategory, setProductCategory]       = useState('');
  const [newCategoryInput, setNewCategoryInput]     = useState('');
  const [productPrice, setProductPrice]             = useState('');
  const [productType, setProductType]               = useState('physical');
  const [subdisciplinesInput, setSubdisciplinesInput] = useState('');
  const [colorsInput, setColorsInput]               = useState('');
  const [sizesInput, setSizesInput]                 = useState('');
  const [imageUrl, setImageUrl]                     = useState('');
  const [productProductionCost, setProductProductionCost] = useState('');
  const [editingProduct, setEditingProduct]         = useState(null);
  const [filterCategory, setFilterCategory]         = useState('');
  const [filterType, setFilterType]                 = useState('');
  const [successMsg, setSuccessMsg]                 = useState('');
  const [errorMsg, setErrorMsg]                     = useState('');

  useEffect(() => {
    Promise.all([fetchProducts(), fetchCategories()]).finally(() => setLoading(false));
  }, []);

  // FIX: validar que la respuesta sea array antes de setear
  const fetchProducts = async () => {
    try {
      const res = await axios.get('/api/list_products.php?full=1', { withCredentials: true });
      setProducts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error al cargar productos:', err);
      setProducts([]);
    }
  };

  // FIX: validar que la respuesta sea array antes de setear
  const fetchCategories = async () => {
    try {
      const res = await axios.get('/api/list_categories.php', { withCredentials: true });
      setCategories(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error al cargar categorías:', err);
      setCategories([]);
    }
  };

  const productTypeOptions = [
    { value: 'physical',   label: 'Físico (Uniforme, Accesorio)' },
    { value: 'service',    label: 'Servicio (Inscripción)'       },
    { value: 'discipline', label: 'Disciplina'                   },
  ];

  const typeLabel = { physical: 'Físico', service: 'Servicio', discipline: 'Disciplina' };

  const showMessage = (type, msg) => {
    if (type === 'success') { setSuccessMsg(msg); setErrorMsg(''); }
    else                    { setErrorMsg(msg);   setSuccessMsg(''); }
    setTimeout(() => { setSuccessMsg(''); setErrorMsg(''); }, 3000);
  };

  const resetForm = () => {
    setProductName('');
    setProductCategory('');
    setNewCategoryInput('');
    setProductPrice('');
    setProductType('physical');
    setSubdisciplinesInput('');
    setColorsInput('');
    setSizesInput('');
    setImageUrl('');
    setProductProductionCost('');
    setEditingProduct(null);
  };

  const handleAddOrUpdateProduct = async (e) => {
    e.preventDefault();
    const finalCategory = newCategoryInput.trim() || productCategory;

    if (!productName.trim()) { showMessage('error', 'El nombre del producto es obligatorio.'); return; }
    if (!finalCategory)      { showMessage('error', 'Selecciona o crea una categoría.');       return; }
    if (!productPrice)       { showMessage('error', 'El precio es obligatorio.');              return; }

    const parsedProductionCost = productProductionCost.trim() === ''
      ? 0 : parseFloat(productProductionCost);
    if (isNaN(parsedProductionCost)) {
      showMessage('error', 'El costo de producción debe ser un número válido.');
      return;
    }

    const productData = {
      name:           productName.trim(),
      category:       newCategoryInput.trim() || undefined,
      category_id:    newCategoryInput.trim() === '' ? productCategory : undefined,
      price:          parseFloat(productPrice),
      type:           productType,
      image:          imageUrl,
      subdisciplines: subdisciplinesInput.split(',').map(s => s.trim()).filter(Boolean),
      colors:         colorsInput.split(',').map(c => c.trim()).filter(Boolean),
      sizes:          sizesInput.split(',').map(s => s.trim()).filter(Boolean),
      productionCost: parsedProductionCost,
    };

    try {
      const endpoint = editingProduct ? '/api/update_product.php' : '/api/create_product.php';
      const payload  = editingProduct ? { ...productData, id: editingProduct.id } : productData;
      const res      = await axios.post(endpoint, payload, { withCredentials: true });

      if (res.data?.success === false) {
        showMessage('error', res.data.message || 'Error al guardar el producto.');
        return;
      }

      showMessage('success', editingProduct ? 'Producto actualizado.' : 'Producto creado.');
      resetForm();
      fetchProducts();
      fetchCategories();
    } catch (err) {
      console.error('Error al guardar producto:', err);
      showMessage('error', err.response?.data?.message || 'Error de conexión.');
    }
  };

  const handleEditClick = (product) => {
    setEditingProduct(product);
    setProductCategory(product.category_id ? String(product.category_id) : '');
    setNewCategoryInput('');
    setProductName(product.name);
    setProductPrice(product.price.toString());
    setProductType(product.type || 'physical');
    setImageUrl(product.imageUrl || '');
    setSubdisciplinesInput(Array.isArray(product.subdisciplines) ? product.subdisciplines.join(', ') : '');
    setColorsInput(Array.isArray(product.colors)  ? product.colors.join(', ')  : '');
    setSizesInput(Array.isArray(product.sizes)    ? product.sizes.join(', ')   : '');
    setProductProductionCost(product.productionCost !== undefined ? product.productionCost.toString() : '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteClick = async (productId) => {
    if (!window.confirm('¿Eliminar este producto?')) return;
    try {
      const res = await axios.post('/api/delete_product.php', { id: productId }, { withCredentials: true });
      if (res.data?.success === false) {
        showMessage('error', res.data.message || 'Error al eliminar.');
        return;
      }
      showMessage('success', 'Producto eliminado.');
      fetchProducts();
    } catch (err) {
      console.error('Error al eliminar producto:', err);
      showMessage('error', 'Error de conexión al eliminar.');
    }
  };

  // Filtrado en el frontend
  const filteredProducts = products.filter(p => {
    const matchCat  = filterCategory ? String(p.category_id) === filterCategory : true;
    const matchType = filterType     ? p.type === filterType                     : true;
    return matchCat && matchType;
  });

  if (loading) {
    return (
      <SharedCard title="Gestión de Productos">
        <p className="text-center text-gray-500 py-8">Cargando productos...</p>
      </SharedCard>
    );
  }

  return (
    <SharedCard title="Gestión de Productos">

      {/* ── Mensajes globales ── */}
      {successMsg && <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">{successMsg}</div>}
      {errorMsg   && <div className="mb-4 p-3 bg-red-100   text-red-700   rounded">{errorMsg}</div>}

      {/* ── Formulario ── */}
      <form onSubmit={handleAddOrUpdateProduct} className="mb-8">
        <h3 className="text-xl font-semibold text-black mb-4">
          {editingProduct ? '✏️ Editar Producto' : '➕ Nuevo Producto'}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SharedInput
            label="Nombre del Producto*"
            value={productName}
            onChange={e => setProductName(e.target.value)}
            placeholder="Ej: Malla de Ballet"
          />
          <SharedInput
            label="Precio*"
            type="number"
            value={productPrice}
            onChange={e => setProductPrice(e.target.value)}
            placeholder="Ej: 300.00"
          />
          <SharedInput
            label="Costo de Producción"
            type="number"
            value={productProductionCost}
            onChange={e => setProductProductionCost(e.target.value)}
            placeholder="Ej: 100.00 (0 si no aplica)"
          />
          <SharedSelect
            label="Tipo de Producto"
            value={productType}
            onChange={e => setProductType(e.target.value)}
            options={productTypeOptions}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <SharedSelect
            label="Categoría existente"
            value={productCategory}
            onChange={e => { setProductCategory(e.target.value); setNewCategoryInput(''); }}
            options={[
              { value: '', label: 'Selecciona una categoría' },
              ...categories.map(cat => ({ value: String(cat.id), label: cat.name }))
            ]}
          />
          <SharedInput
            label="O crear nueva categoría"
            value={newCategoryInput}
            onChange={e => { setNewCategoryInput(e.target.value); setProductCategory(''); }}
            placeholder="Ej: Accesorios de Danza"
          />
        </div>

        {productType === 'discipline' && (
          <div className="mt-4">
            <SharedInput
              label="Subdisciplinas (separadas por coma)"
              value={subdisciplinesInput}
              onChange={e => setSubdisciplinesInput(e.target.value)}
              placeholder="Ej: Ballet Clásico I, Puntas"
            />
          </div>
        )}

        {productType === 'physical' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <SharedInput
              label="Colores (separados por coma)"
              value={colorsInput}
              onChange={e => setColorsInput(e.target.value)}
              placeholder="Ej: Rojo, Azul, Negro"
            />
            <SharedInput
              label="Tallas (separadas por coma)"
              value={sizesInput}
              onChange={e => setSizesInput(e.target.value)}
              placeholder="Ej: CH, M, G, XL"
            />
          </div>
        )}

        <div className="mt-4">
          <ImageUploader
            label="Imagen del Producto"
            imageUrl={imageUrl}
            onImageUrlChange={setImageUrl}
            onFileChange={setImageUrl}
          />
        </div>

        <div className="flex gap-3 mt-6">
          <SharedButton type="submit">
            {editingProduct ? 'Actualizar Producto' : 'Agregar Producto'}
          </SharedButton>
          {editingProduct && (
            <SharedButton onClick={resetForm} className="bg-gray-600 hover:bg-gray-700">
              Cancelar Edición
            </SharedButton>
          )}
        </div>
      </form>

      {/* ── Filtros ── */}
      <div className="flex flex-wrap gap-4 mb-4">
        <div className="flex items-center gap-2">
          <label className="text-sm font-semibold text-black">Filtrar por categoría:</label>
          <select
            value={filterCategory}
            onChange={e => setFilterCategory(e.target.value)}
            className="px-3 py-1 border rounded bg-gray-100 text-black text-sm"
          >
            <option value="">Todas</option>
            {categories.map(cat => (
              <option key={cat.id} value={String(cat.id)}>{cat.name}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-semibold text-black">Filtrar por tipo:</label>
          <select
            value={filterType}
            onChange={e => setFilterType(e.target.value)}
            className="px-3 py-1 border rounded bg-gray-100 text-black text-sm"
          >
            <option value="">Todos</option>
            {productTypeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        {(filterCategory || filterType) && (
          <button
            onClick={() => { setFilterCategory(''); setFilterType(''); }}
            className="text-xs text-red-600 underline"
          >
            Limpiar filtros
          </button>
        )}
      </div>

      {/* ── Tabla ── */}
      <h3 className="text-xl font-semibold text-black mb-4">
        Productos ({filteredProducts.length})
      </h3>

      {filteredProducts.length === 0 ? (
        <p className="text-black text-center py-6">No hay productos registrados.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-gray-100 rounded-lg shadow-md">
            <thead>
              <tr className="bg-[#961914] text-white uppercase text-sm leading-normal">
                <th className="py-3 px-4 text-left">Nombre</th>
                <th className="py-3 px-4 text-left">Categoría</th>
                <th className="py-3 px-4 text-left">Precio</th>
                <th className="py-3 px-4 text-left">Costo</th>
                <th className="py-3 px-4 text-left">Tipo</th>
                <th className="py-3 px-4 text-left">Variantes</th>
                <th className="py-3 px-4 text-left">Imagen</th>
                <th className="py-3 px-4 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody className="text-black text-sm">
              {filteredProducts.map(product => (
                <tr key={product.id} className="border-b border-gray-300 hover:bg-gray-200 transition-colors duration-150">
                  <td className="py-3 px-4 font-medium whitespace-nowrap">{product.name}</td>
                  <td className="py-3 px-4">{product.category_name || '-'}</td>
                  <td className="py-3 px-4">${parseFloat(product.price || 0).toFixed(2)}</td>
                  <td className="py-3 px-4">
                    {product.productionCost > 0
                      ? `$${product.productionCost.toFixed(2)}`
                      : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold
                      ${product.type === 'physical'   ? 'bg-blue-100 text-blue-700'   : ''}
                      ${product.type === 'service'    ? 'bg-green-100 text-green-700' : ''}
                      ${product.type === 'discipline' ? 'bg-purple-100 text-purple-700' : ''}
                    `}>
                      {typeLabel[product.type] || product.type}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-xs text-gray-600">
                    {product.type === 'discipline' && Array.isArray(product.subdisciplines) && product.subdisciplines.length > 0
                      ? product.subdisciplines.join(', ')
                      : null}
                    {product.type === 'physical' && (
                      <div className="space-y-0.5">
                        {Array.isArray(product.colors) && product.colors.length > 0 &&
                          <div><span className="font-semibold">Col:</span> {product.colors.join(', ')}</div>}
                        {Array.isArray(product.sizes) && product.sizes.length > 0 &&
                          <div><span className="font-semibold">Tal:</span> {product.sizes.join(', ')}</div>}
                      </div>
                    )}
                    {product.type === 'service' && <span className="text-gray-400">—</span>}
                  </td>
                  <td className="py-3 px-4">
                    {product.imageUrl
                      ? <img src={product.imageUrl} alt={product.name} className="h-16 w-16 object-contain rounded-md shadow" />
                      : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex justify-center gap-2">
                      <SharedButton
                        onClick={() => handleEditClick(product)}
                        className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700"
                      >
                        Editar
                      </SharedButton>
                      <SharedButton
                        onClick={() => handleDeleteClick(product.id)}
                        className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700"
                      >
                        Eliminar
                      </SharedButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SharedCard>
  );
};

export default ProductManagementForm;